import "./tailwind.css";
import "react-tooltip/dist/react-tooltip.css";
import { json, type LinksFunction } from "@remix-run/node";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError,
} from "@remix-run/react";
import { RootContext } from "./root_context";
import { useState } from "react";
import { Provider as UrqlProvider } from "urql";
import mySvgHref from "./components/layout_mira_clone/logo.svg";
import { getGraphqlClientSideClient } from "./gql/gqlclient_client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

export async function loader() {
  return json({
    // Build version (commit SHA) injected at deploy via BUILD_VERSION; falls
    // back to "dev" for local runs. Shown in the sidebar footer.
    version: process.env.BUILD_VERSION || "dev",
    PUBLIC_ENV: {
      CPRM_API_URL: process.env.CPRM_API_URL,
      CPRM_GRAPH_URL: process.env.CPRM_GRAPH_URL,
      STUDENT_CHECKIN_URL: process.env.STUDENT_CHECKIN_URL,
    },
  });
}

export const links: LinksFunction = () => {
  return [
    {
      rel: "icon",
      type: "image/x-icon",
      href: "/favicon.ico",
    },
    {
      rel: "shortcut icon",
      type: "image/x-icon",
      href: "/favicon.ico",
    },
    {
      rel: "apple-touch-icon",
      sizes: "180x180",
      href: "/assets/images/favicon/apple-touch-icon.png",
    },
    {
      rel: "icon",
      type: "image/png",
      sizes: "192x192",
      href: "/assets/images/favicon/android-chrome-192x192.png",
    },
    {
      rel: "icon",
      type: "image/png",
      sizes: "512x512",
      href: "/assets/images/favicon/android-chrome-512x512.png",
    },
    {
      rel: "icon",
      type: "image/png",
      sizes: "32x32",
      href: "/assets/images/favicon/favicon-32x32.png",
    },
    {
      rel: "icon",
      type: "image/png",
      sizes: "16x16",
      href: "/assets/images/favicon/favicon-16x16.png",
    },
  ];
};

export function ErrorBoundary() {
  const error = useRouteError() as Error;
  return (
    <html>
      <head>
        <title>Oh no!</title>
        <Meta />
        <Links />
      </head>
      <body className="flex h-screen w-full">
        <div className="flex-1 bg-blue-700 w-full h-full">
          <div className="flex flex-col items-center justify-center w-full h-full">
            <div className="bg-white max-w-[75vw] p-4">
              <div className="text-5xl font-bold text-center mt-10 flex justify-center">
                <img src={mySvgHref} className="w-12 h-12 mr-2" />
                CPRM
              </div>
              <div className="text-2xl m-5 text-center">Instructor Portal</div>

              <p className="text-center pb-6 font-bold">
                An error occurred, please contact support. Details below:
              </p>
              {isRouteErrorResponse(error) && (
                <>
                  <p className="max-w-[100%] overflow-auto">
                    {error.status} {error.statusText}
                  </p>
                  <p className="font-bold">Additional Information:</p>
                  <pre className="max-w-[100%] max-h-[35vh] overflow-auto">
                    {error.data}
                  </pre>
                </>
              )}
              {!isRouteErrorResponse(error) && (
                <>
                  <p className="max-w-[100%] overflow-auto">
                    <span className="font-bold">Error Message:</span>{" "}
                    {error.message}
                  </p>
                  <p className="font-bold">Additional Information:</p>
                  <pre className="max-w-[100%] max-h-[35vh] overflow-auto">
                    {error.stack}
                  </pre>
                </>
              )}
            </div>
          </div>
        </div>

        <Scripts />
      </body>
    </html>
  );
}

const queryClient = new QueryClient();

export default function App() {
  const { PUBLIC_ENV } = useLoaderData<typeof loader>();
  const [bodyStyles, setBodyStyles] = useState("");
  if (!(typeof document === "undefined")) {
    // @ts-ignore
    window.__CPRM_API_URL__ = PUBLIC_ENV.CPRM_API_URL!;
    // @ts-ignore
    window.__CPRM_GRAPH_URL__ = PUBLIC_ENV.CPRM_GRAPH_URL!;
    // @ts-ignore
    window.__STUDENT_CHECKIN_URL__ = PUBLIC_ENV.STUDENT_CHECKIN_URL!;
  }

  return (
    <html lang="en" className="h-full bg-white">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <RootContext.Provider
        value={{
          bodyStyles,
          cprmApiUrl: PUBLIC_ENV.CPRM_API_URL!,
          setBodyStyles,
        }}
      >
        <QueryClientProvider client={queryClient}>
          <UrqlProvider value={getGraphqlClientSideClient()}>
            <body
              className={bodyStyles}
              style={{
                color: "rgba(0, 0, 0, 0.87)",
                fontSize: "13px",
                fontWeight: 400,
                lineHeight: 1.5,
                backgroundColor: "#F7F9FC",
              }}
            >
              <Outlet />
              <ScrollRestoration />
              <Scripts />
            </body>
          </UrqlProvider>
        </QueryClientProvider>
      </RootContext.Provider>
    </html>
  );
}
